import * as React from "react";
import { Box, Typography, Checkbox } from "@mui/material";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ContractorTableProps {
  labourMeasurements: any[];
  onOrderStatusChange?: (
    productSystemId: number,
    statusType: 'orderSent' | 'orderConfirmed',
    value: boolean,
    currentOrderSent: boolean,
    currentOrderConfirmed: boolean,
    productSystemName?: string
  ) => void;
  onChangeOrderSent?: (
    productSystemId: number,
    productSystemName?: string,
    existingRemovalDate?: string,
    existingInstallationDate?: string
  ) => void;
}

const truncateText = (text: string, length: number) => {
  if (!text) return ""; // Return empty string if no text
  return text.length > length ? text.substring(0, length) + "..." : text;
};

export const ContractorTable: React.FC<ContractorTableProps> = ({
  labourMeasurements,
  onOrderStatusChange,
  onChangeOrderSent,
}) => {
  // Handle order status change
  const handleOrderStatusChange = (
    productSystemId: number,
    statusType: 'orderSent' | 'orderConfirmed',
    value: boolean,
    currentOrderSent: boolean,
    currentOrderConfirmed: boolean,
    productSystemName?: string
  ) => {
    // Call the parent callback if provided
    if (onOrderStatusChange) {
      onOrderStatusChange(productSystemId, statusType, value, currentOrderSent, currentOrderConfirmed, productSystemName);
    }
  };
  const Table = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {labourMeasurements?.some((labourCategory) => {
            const firstMeasurement = labourCategory?.measurements[0];
            const selectedContractor =
              firstMeasurement?.associatedContractor?.filter(
                (contractor: any) => contractor?.isSelected === true
              );
            return selectedContractor?.length > 0;
          }) ? (
            labourMeasurements?.map((labourCategory, index) => {
              const firstMeasurement = labourCategory?.measurements[0];

              const selectedContractor =
                firstMeasurement?.associatedContractor?.filter(
                  (contractor: any) => contractor?.isSelected === true
                );

              if (selectedContractor?.length === 0) {
                return null;
              }

              return (
                <Box key={index}>
                  {selectedContractor?.length > 0 && (
                    <>
                      {/* Product System Header */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          alignItems: { xs: "flex-start", md: "center" },
                          justifyContent: "space-between",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: 18, md: 20 },
                            fontWeight: 700,
                            color: "#1f2937",
                            borderBottom: "3px solid #4b87ed",
                            pb: 0.5,
                          }}
                        >
                          {labourCategory?.name}
                        </Typography>

                        {/* Order Status Controls */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                          <Box
                            onClick={() =>
                              handleOrderStatusChange(
                                labourCategory.productId,
                                "orderSent",
                                !labourCategory.orderSent,
                                labourCategory.orderSent || false,
                                labourCategory.orderConfirmed || false,
                                labourCategory.name
                              )
                            }
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 2,
                              py: 1,
                              borderRadius: "8px",
                              backgroundColor: labourCategory.orderSent
                                ? "#dcfce7"
                                : "#f3f4f6",
                              border: `2px solid ${
                                labourCategory.orderSent ? "#22c55e" : "#e5e7eb"
                              }`,
                              cursor: "pointer",
                              transition: "all 0.2s",
                              "&:hover": {
                                backgroundColor: labourCategory.orderSent
                                  ? "#bbf7d0"
                                  : "#e5e7eb",
                              },
                            }}
                          >
                            <Checkbox
                              checked={labourCategory.orderSent || false}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                padding: 0,
                                color: "#9ca3af",
                                "&.Mui-checked": {
                                  color: "#22c55e",
                                },
                              }}
                              size="small"
                            />
                            <LocalShippingIcon
                              sx={{
                                fontSize: 18,
                                color: labourCategory.orderSent
                                  ? "#22c55e"
                                  : "#6b7280",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: labourCategory.orderSent
                                  ? "#166534"
                                  : "#374151",
                              }}
                            >
                              Order Sent
                            </Typography>
                          </Box>

                          <Box
                            onClick={() => {
                              if (labourCategory.orderSent) {
                                handleOrderStatusChange(
                                  labourCategory.productId,
                                  "orderConfirmed",
                                  !labourCategory.orderConfirmed,
                                  labourCategory.orderSent || false,
                                  labourCategory.orderConfirmed || false,
                                  labourCategory.name
                                );
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 2,
                              py: 1,
                              borderRadius: "8px",
                              backgroundColor: labourCategory.orderConfirmed
                                ? "#dcfce7"
                                : "#f3f4f6",
                              border: `2px solid ${
                                labourCategory.orderConfirmed
                                  ? "#22c55e"
                                  : "#e5e7eb"
                              }`,
                              cursor: labourCategory.orderSent
                                ? "pointer"
                                : "not-allowed",
                              opacity: labourCategory.orderSent ? 1 : 0.5,
                              transition: "all 0.2s",
                              "&:hover": labourCategory.orderSent
                                ? {
                                    backgroundColor: labourCategory.orderConfirmed
                                      ? "#bbf7d0"
                                      : "#e5e7eb",
                                  }
                                : {},
                            }}
                          >
                            <Checkbox
                              checked={labourCategory.orderConfirmed || false}
                              disabled={!labourCategory.orderSent}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                padding: 0,
                                color: "#9ca3af",
                                "&.Mui-checked": {
                                  color: "#22c55e",
                                },
                                "&.Mui-disabled": {
                                  color: "#d1d5db",
                                },
                              }}
                              size="small"
                            />
                            <CheckCircleIcon
                              sx={{
                                fontSize: 18,
                                color: labourCategory.orderConfirmed
                                  ? "#22c55e"
                                  : "#6b7280",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: labourCategory.orderConfirmed
                                  ? "#166534"
                                  : "#374151",
                              }}
                            >
                              Order Confirmed
                            </Typography>
                          </Box>

                          {/* Change Order Sent Button - only show when order is sent */}
                          {labourCategory.orderSent && onChangeOrderSent && (
                            <Box
                              onClick={(e) => {
                                e.preventDefault();
                                onChangeOrderSent(
                                  labourCategory.productId,
                                  labourCategory.name,
                                  labourCategory.removalDate,
                                  labourCategory.installationDate
                                );
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 2,
                                py: 1,
                                borderRadius: "8px",
                                backgroundColor: "#f3f4f6",
                                border: "2px solid #3b82f6",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": {
                                  backgroundColor: "#dbeafe",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#3b82f6",
                                }}
                              >
                                Change Order Sent
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Desktop Table View */}
                      <Box
                        sx={{
                          display: { xs: "none", md: "block" },
                          overflowX: "auto",
                          border: "1px solid #e5e7eb",
                          borderRadius: "12px",
                          mb: 3,
                        }}
                      >
                        <Box
                          component="table"
                          sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            minWidth: "600px",
                          }}
                        >
                          <Box component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                            <Box component="tr">
                              <Box
                                component="th"
                                sx={{
                                  py: 2,
                                  px: 2,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  textAlign: "left",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Name
                              </Box>
                              <Box
                                component="th"
                                sx={{
                                  py: 2,
                                  px: 2,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  textAlign: "left",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Description
                              </Box>
                              <Box
                                component="th"
                                sx={{
                                  py: 2,
                                  px: 2,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  textAlign: "left",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Website
                              </Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {selectedContractor?.map((labour, idx) => (
                              <Box
                                component="tr"
                                key={idx}
                                sx={{
                                  borderTop: "1px solid #e5e7eb",
                                  "&:hover": {
                                    backgroundColor: "#f9fafb",
                                  },
                                }}
                              >
                                <Box
                                  component="td"
                                  sx={{
                                    py: 2,
                                    px: 2,
                                    fontSize: 14,
                                    color: "#1f2937",
                                    fontWeight: 500,
                                  }}
                                >
                                  {labour?.name || "N/A"}
                                </Box>
                                <Box
                                  component="td"
                                  sx={{
                                    py: 2,
                                    px: 2,
                                    fontSize: 14,
                                    color: "#6b7280",
                                  }}
                                >
                                  {labour?.description || "N/A"}
                                </Box>
                                <Box
                                  component="td"
                                  sx={{
                                    py: 2,
                                    px: 2,
                                    fontSize: 14,
                                    color: "#4b87ed",
                                  }}
                                >
                                  {labour?.website ? (
                                    <Box
                                      component="a"
                                      href={labour.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        color: "#4b87ed",
                                        textDecoration: "none",
                                        "&:hover": {
                                          textDecoration: "underline",
                                        },
                                      }}
                                    >
                                      {labour.website}
                                    </Box>
                                  ) : (
                                    "N/A"
                                  )}                              </Box>
                              </Box>
                            ))}                          </Box>
                        </Box>
                      </Box>

                      {/* Mobile Card View */}
                      <Box
                        sx={{
                          display: { xs: "flex", md: "none" },
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {selectedContractor?.map((labour, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              backgroundColor: "white",
                              border: "1px solid #e5e7eb",
                              borderRadius: "12px",
                              p: 2,
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            }}
                          >
                            <Box sx={{ display: "grid", gap: 1.5 }}>
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    mb: 0.5,
                                  }}
                                >
                                  Name
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#1f2937",
                                  }}
                                >
                                  {labour?.name || "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    mb: 0.5,
                                  }}
                                >
                                  Description
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 14, color: "#6b7280" }}
                                >
                                  {labour?.description || "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    mb: 0.5,
                                  }}
                                >
                                  Website
                                </Typography>
                                {labour?.website ? (
                                  <Box
                                    component="a"
                                    href={labour.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      fontSize: 14,
                                      color: "#4b87ed",
                                      textDecoration: "none",
                                      wordBreak: "break-word",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {labour.website}
                                  </Box>
                                ) : (
                                  <Typography
                                    sx={{ fontSize: 14, color: "#6b7280" }}
                                  >
                                    N/A
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}                          </Box>
                    </>
                  )}
                </Box>
              );
            })
          ) : (
            <Box
              sx={{
                p: 6,
                textAlign: "center",
                fontSize: 14,
                color: "#6b7280",
                backgroundColor: "#f9fafb",
                borderRadius: "12px",
              }}
            >
              No data available
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return Table();
};
